/** @jsxImportSource @emotion/react */
"use client";

import { useModal } from "@ebay/nice-modal-react";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  getContentAlbumImage,
  getContentDescription,
  getContentHasVideo,
  getContentMetadata,
  getContentTitle,
  getShareProperties,
} from "@packages/media";
import type { QueueManagedItem } from "@packages/sdk";
import {
  useBranch,
  useRedirects,
  useRequestFavorite,
  useRequestPrayer,
  useSession,
} from "@packages/sdk";
import { useContext } from "react";

import {
  colors,
  spacing,
  ThemeProvider,
  zIndices,
} from "../../../../../../global/emotion";
import { PlayerInternalContext } from "../../../../../media/components/core/MediaPlayer/MediaPlayer.emotion";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import { IconButton } from "../../../_core/IconButton/IconButton.emotion";
import { ShareIcon, StarIcon } from "../../../icons/Icon/GeneralIcons";
import { MoreIcon } from "../../../icons/Icon/NavigationIcons";
import { DropdownMenu } from "../../../modals/DropdownMenu/DropdownMenu.emotion";
import { DropdownMenuItem } from "../../../modals/DropdownMenuItem/DropdownMenuItem.emotion";
import { ShareModal } from "../../../modals/ShareModal/ShareModal.emotion";
import ContentMoreMenuHeader from "./ContentMoreMenuHeader.emotion";

const dropdownMenuContentStyle: Interpolation = { paddingTop: spacing.l };
const modalZStyle: Interpolation = {
  zIndex: zIndices.modalOverQueue,
};
const noBackgroundStyle: Interpolation = {
  background: colors.transparent,
};
const modalPaddingStyle: Interpolation = {
  paddingTop: spacing.l,
};

export type MediaMoreMenuProps = Omit<HallowElementProps<"div">, "content"> & {
  content: QueueManagedItem;
  onOpenChange?: (open: boolean) => void;
};

export const MediaMoreMenu = ({
  content,
  onOpenChange,
  ...props
}: MediaMoreMenuProps) => {
  const { mutationPost: updateFavorite } = useRequestFavorite({
    id: content?.type === "prayer" ? content.prayer.id : null,
  });
  const { query: prayerQuery } = useRequestPrayer({
    id: content?.type === "prayer" ? content.prayer.id : null,
  });
  const { status } = useSession();
  const shareModal = useModal(ShareModal);
  const branch = useBranch();
  const analytics = useAnalytics();
  const t = useTranslations();
  const { popupRef } = useContext(PlayerInternalContext);
  const { redirectToAuth } = useRedirects();

  const onClickFavorite = () => {
    if (status === "authenticated") updateFavorite.mutate();
    else redirectToAuth();
  };

  return (
    <ThemeProvider forceTheme="dark" as={null}>
      <div css={noBackgroundStyle} onClick={(e) => e.stopPropagation()}>
        <DropdownMenu
          trigger={
            <IconButton
              icon={<MoreIcon />}
              onClick={(e) => {
                // The stopImmediatePropagation call prevents next top loader from firing unnecessarily
                e.nativeEvent.stopImmediatePropagation();
                e.preventDefault();
              }}
              variant="transparentWhite"
            />
          }
          align="end"
          contentCSS={[dropdownMenuContentStyle, modalZStyle]}
          contentProps={props}
          modal={false}
          onOpenChange={onOpenChange}
          portalProps={{ container: popupRef.current }}
        >
          <ContentMoreMenuHeader
            title={getContentTitle(content)}
            subtitle={getContentDescription(content)}
            metadata={getContentMetadata(content)}
            imageSrc={getContentAlbumImage(content)}
            hasVideoContent={getContentHasVideo(content)}
          />
          {content?.type !== "radio_song" ? (
            <DropdownMenuItem
              startIcon={
                prayerQuery.data?.is_favorite ? (
                  <StarIcon.Off />
                ) : (
                  <StarIcon.Outline />
                )
              }
              onClick={onClickFavorite}
            >
              {prayerQuery.data?.is_favorite
                ? t("dialog_prayer_options_unfavorite")
                : t("dialog_prayer_options_favorite")}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            startIcon={<ShareIcon />}
            onClick={async () => {
              const event =
                content?.type === "prayer"
                  ? "Tapped Share Prayer"
                  : content?.type === "radio_song"
                    ? "Tapped Share Radio Station"
                    : null;
              if (event) {
                const props: Record<string, number> = {};
                if (content.type === "prayer") props.prayer = content.prayer.id;
                else if (content.type === "radio_song")
                  props.radio_station = content.radio_station.id;
                analytics.track({
                  event,
                  ...props,
                });
              }
              const shareProps = await getShareProperties(content, branch, [
                modalZStyle,
                modalPaddingStyle,
              ]);
              return shareModal.show({
                ...shareProps,
                overlayProps: {
                  onPointerDown: () => {
                    analytics.track({
                      event: HallowAnalyticsEvent.ClosedShareAudioView,
                    });
                  },
                },
                portalProps: { container: popupRef.current },
              });
            }}
          >
            {t("dialog_prayer_options_share")}
          </DropdownMenuItem>
        </DropdownMenu>
      </div>
    </ThemeProvider>
  );
};
